import { AACheckoutConst } from '@/helper/constants';
import CabinDataManager from './cabin-data-manager';

export default class FlatbedBundleEffect {
  private cabinManager: CabinDataManager;

  constructor(cabinManager: CabinDataManager) {
    this.cabinManager = cabinManager;
  }

  async handleBundlePackageChange(
    bundleData: any,
    currentTripsInfo: TripsInfo,
  ): Promise<{
    pageData: any;
    tripsInfo: TripsInfo;
  }> {
    const tripsInfo = this.createTripsInfoFromBundlePackage(
      bundleData,
      currentTripsInfo,
    );

    return this.cabinManager.getCabinData(tripsInfo);
  }

  async checkBundleOriginData(
    originBundleData: any,
    currentTripsInfo: TripsInfo,
  ): Promise<{
    pageData: any;
    tripsInfo: TripsInfo;
  } | null> {
    const tripsInfo = this.createTripsInfoFromOriginData(
      originBundleData,
      currentTripsInfo,
    );
    return tripsInfo ? this.cabinManager.getCabinData(tripsInfo) : null;
  }

  private createTripsInfoFromOriginData(
    originBundleData: any,
    currentTripsInfo: TripsInfo,
  ): TripsInfo {
    const { departAncillaryList, returnAncillaryList } = originBundleData;
    const { departCabinClass, returnCabinClass } = currentTripsInfo;

    const hasDepartFlatbed = departAncillaryList?.some(
      (ancillary) =>
        ancillary.bundleName === AACheckoutConst.FLATBED_BUNDLE_NAME,
    );
    const hasReturnFlatbed = returnAncillaryList?.some(
      (ancillary) =>
        ancillary.bundleName === AACheckoutConst.FLATBED_BUNDLE_NAME,
    );

    // Only pass true for flatbed if current cabin is business and has flatbed
    const newDepartFlatbed =
      departCabinClass === AACheckoutConst.CABIN_CLASS.C && hasDepartFlatbed;
    const newReturnFlatbed =
      returnCabinClass === AACheckoutConst.CABIN_CLASS.C && hasReturnFlatbed;

    return this.createTripsInfo(
      currentTripsInfo,
      newDepartFlatbed,
      newReturnFlatbed,
    );
  }

  private createTripsInfoFromBundlePackage(
    bundleData: any,
    currentTripsInfo: TripsInfo,
  ): TripsInfo {
    const isDepartFlatbed =
      !this.isEmptyValue(bundleData.departFlight) &&
      this.isFlatbedBundle(bundleData.departFlight);
    const isReturnFlatbed =
      !this.isEmptyValue(bundleData.returnFlight) &&
      this.isFlatbedBundle(bundleData.returnFlight);

    return this.createTripsInfo(
      currentTripsInfo,
      isDepartFlatbed,
      isReturnFlatbed,
    );
  }

  private createTripsInfo(
    currentTripsInfo: TripsInfo,
    isDepartFlatbed: boolean,
    isReturnFlatbed: boolean,
  ): TripsInfo {
    return {
      ...currentTripsInfo,
      departCabinClass: isDepartFlatbed
        ? AACheckoutConst.CABIN_CLASS.C
        : AACheckoutConst.CABIN_CLASS.M,
      ...(currentTripsInfo.isRoundTrip && {
        returnCabinClass: isReturnFlatbed
          ? AACheckoutConst.CABIN_CLASS.C
          : AACheckoutConst.CABIN_CLASS.M,
      }),
      extendedParams: {
        ...currentTripsInfo.extendedParams,
        isDepartCabinBusiness: isDepartFlatbed,
        isReturnCabinBusiness: isReturnFlatbed,
      },
    };
  }

  private isFlatbedBundle(bundleData: any): boolean {
    return bundleData?.bundleName === AACheckoutConst.FLATBED_BUNDLE_NAME;
  }

  private isEmptyValue(value: any): boolean {
    return (
      value === undefined ||
      value === null ||
      (typeof value === 'object' && Object.keys(value).length === 0)
    );
  }
}
